import { BlotterDensity, BlotterTable, useBlotterObservables, useBlotterTable } from '@talos/kyoko';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import type { TotalCostRiskData } from '../../../types';
import { TOTAL_COST_RISK_COLORS } from './totalCostRiskColors';
import { useTotalCostRiskColumns, type TotalCostRiskRow } from './useTotalCostRiskColumns';

interface TotalCostRiskBreakdownTableProps {
  data: TotalCostRiskData;
}

export function TotalCostRiskBreakdownTable({ data }: TotalCostRiskBreakdownTableProps) {
  const theme = useTheme();

  const maxValue = useMemo(() => {
    const max = Math.max(data.spreadCost, data.tempCost, data.risk);
    return max > 0 ? max : null;
  }, [data]);

  const rows = useMemo<TotalCostRiskRow[]>(() => {
    return [
      { name: 'Spread Cost', value: data.spreadCost, dataTestId: 'total-cost-risk-row-spread-cost' },
      { name: 'Temp. Cost', value: data.tempCost, dataTestId: 'total-cost-risk-row-temp-cost' },
      { name: 'Risk', value: data.risk, dataTestId: 'total-cost-risk-row-risk' },
    ];
  }, [data]);

  const columns = useTotalCostRiskColumns({
    maxValue,
    colors: TOTAL_COST_RISK_COLORS,
  });

  const { dataObservable, errorObservable } = useBlotterObservables({
    data: rows,
  });

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable,
    density: BlotterDensity.Compact,
    columns,
    rowID: 'name',
    gridOptions: {
      domLayout: 'autoHeight',
      autoSizeStrategy: {
        type: 'fitGridWidth',
      },
      getRowStyle: () => {
        return { background: theme.backgroundCard, borderBottom: 'none' };
      },
      processRowPostCreate: ({ node, eRow }) => {
        const dataTestId = node.data?.dataTestId;
        if (dataTestId) {
          eRow.setAttribute('data-testid', dataTestId);
        }
      },
    },
  });

  return <BlotterTable {...blotterTable} headerBackground={theme.backgroundCard} hideHeaderBorder />;
}
